import { openCameraCapture } from '../camera-capture/openCameraCapture';
import { useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePersonImage } from '../../hooks/usePersonImage';
import { usePersonImages } from '../../hooks/usePersonImages';
import {
    useRemovePersonBgFromUploadMutation,
    useSetDefaultPersonImageMutation,
    useUpdatePersonImageNameMutation,
    useTogglePersonImageBackgroundMutation
} from '../../redux/apis/endpoints/personImageApi';
import { useToggleVTOResultBackgroundMutation, useRemoveVTOResultBackgroundMutation } from '../../redux/apis/endpoints/vtoResultApi';
import { selectSelectedVTOResultId } from '../../redux/slices/vtoSlice';
import { useVTOResults } from '../../hooks/useVTOResults';
import { clearTryOnItems } from '../../redux/slices/clothingItemsSlice';
import { clearSelectedVTOResult } from '../../redux/slices/vtoSlice';
import { compressForBackgroundRemoval, processFileForUpload } from '../../utils/imageCompression';
import useVTODeletion from '../../hooks/useVTODeletion';
import { useCredits } from '../../hooks/useCredits';

/**
 * Custom hook that contains all handler logic for PersonImageModal
 * Keeps the main component clean by extracting business logic
 */
const usePersonImageModalHandlers = ({ onClose }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);

    // Local state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [isRemovingBg, setIsRemovingBg] = useState(false);
    const [bgRemovalError, setBgRemovalError] = useState(null);
    const [editingName, setEditingName] = useState(false);
    const [tempName, setTempName] = useState('');
    const [defaultError, setDefaultError] = useState(null);
    const [nameError, setNameError] = useState(null);
    const [isSettingDefault, setIsSettingDefault] = useState(false);
    const [isSavingName, setIsSavingName] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState(null);
    const [isSavingCrop, setIsSavingCrop] = useState(false);

    // VTO deletion warning state
    const [showDeletionWarning, setShowDeletionWarning] = useState(false);
    const [vtoCount, setVtoCount] = useState(0);
    const [deletionError, setDeletionError] = useState(null);

    // BG removal processing modal state
    const [showBgRemovalProcessing, setShowBgRemovalProcessing] = useState(false);

    // Hooks
    const {
        setPersonImage,
        setVirtualTryOnResult,
        revertToOriginal
    } = usePersonImage();

    const { checkVTOCount, deleteWithCascade, isDeleting: isDeletingWithCascade } = useVTODeletion();
    const { balance: creditBalance, hasCredits, daysUntilReset, refetch: refetchCredits } = useCredits();

    const {
        personImages,
        activePersonImage,
        activePersonImageId,
        isLoading: isLoadingImages,
        uploadPersonImage,
        setActivePersonImage,
        clearActivePersonImage,
        deletePersonImage,
        isUploading,
        isDeleting,
        isCanvasIntegrated,
        setCanvasIntegrationMode,
        refetchImages
    } = usePersonImages();

    // Mutations
    const [removePersonBgFromUpload, { isLoading: isRemovingBgUpload }] = useRemovePersonBgFromUploadMutation();
    const [togglePersonImageBackground, { isLoading: isTogglingBg }] = useTogglePersonImageBackgroundMutation();
    const [toggleVTOResultBackground] = useToggleVTOResultBackgroundMutation();
    const [removeVTOResultBackground] = useRemoveVTOResultBackgroundMutation();
    const [setDefaultPersonImage] = useSetDefaultPersonImageMutation();
    const [updatePersonImageName] = useUpdatePersonImageNameMutation();

    // VTO result data
    const selectedVTOResultId = useSelector(selectSelectedVTOResultId);
    const { getVTOResultById } = useVTOResults();
    const selectedVTOResult = selectedVTOResultId ? getVTOResultById(selectedVTOResultId) : null;

    const hasPersonImage = !!activePersonImage;

    // Handlers
    const handleFileInputClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleLocalFileUpload = useCallback(async (event) => {
        const file = event.target.files[0];
        if (file) {
            setIsPhotoUploading(true);
            try {
                console.log('🔄 Person modal upload - processing file');

                const fileToUpload = await processFileForUpload(file, {
                    maxWidth: 2048,
                    maxHeight: 3072,
                    targetSizeMB: 5,
                    thresholdMB: 5,
                    minQuality: 0.5,
                    maxQuality: 0.9
                });

                const isLoggedIn = !!localStorage.getItem('token');

                if (!isLoggedIn) {
                    const imageData = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = () => reject(reader.error);
                        reader.readAsDataURL(fileToUpload);
                    });
                    {
                        const guestPersonImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');

                        const newImage = {
                            id: Date.now(),
                            image: { url: imageData },
                            fileName: file.name,
                            createdAt: new Date().toISOString(),
                            backgroundRemoved: false
                        };

                        guestPersonImages.unshift(newImage);
                        localStorage.setItem('guestPersonImages', JSON.stringify(guestPersonImages));

                        setPersonImage(imageData);
                        setCanvasIntegrationMode(true);

                        console.log('✅ Person image saved to localStorage');
                        setTimeout(() => onClose(), 100);
                    }
                    return;
                }

                await uploadPersonImage({
                    file: fileToUpload,
                    fileName: file.name,
                    backgroundRemoved: false
                });

                console.log('✅ Person image uploaded and set as active');
                onClose();
            } catch (error) {
                console.error('❌ Upload error:', error);
            } finally {
                setIsPhotoUploading(false);
            }
        }
    }, [onClose, setPersonImage, setCanvasIntegrationMode, uploadPersonImage]);

    const handleTakePicture = useCallback(() => {
        onClose();
        openCameraCapture({
            initialFacingMode: 'user',
            onCapture: file => handleLocalFileUpload({ target: { files: [file] } })
        });
    }, [onClose, handleLocalFileUpload]);

    const handleRemovePersonImage = useCallback(() => {
        clearActivePersonImage();
        dispatch(clearTryOnItems());
        dispatch(clearSelectedVTOResult());
        onClose();
    }, [clearActivePersonImage, dispatch, onClose]);

    const handleRemoveBackgroundClick = useCallback(async () => {
        if (!activePersonImage) {
            setBgRemovalError('Please select a person image first.');
            return;
        }

        const isLoggedIn = !!localStorage.getItem('token');
        if (!isLoggedIn) {
            setBgRemovalError('Please log in to use background removal feature.');
            return;
        }

        setShowBgRemovalProcessing(true);

        try {
            setBgRemovalError(null);

            if (selectedVTOResult?.id) {
                console.log('🔄 Removing background from VTO result:', selectedVTOResult.id);
                const vtoResult = await removeVTOResultBackground(selectedVTOResult.id).unwrap();

                const removedBgUrl = Array.isArray(vtoResult?.removedBgImage) ? vtoResult.removedBgImage[0]?.url : vtoResult?.removedBgImage?.url;
                if (removedBgUrl) {
                    console.log('✅ VTO result background removed:', removedBgUrl);

                    setVirtualTryOnResult({
                        image: removedBgUrl,
                        originalImage: activePersonImage?.image?.url || activePersonImage?.removedBgImage?.url,
                        metadata: {
                            isVirtualTryOnResult: true,
                            vtoResultId: vtoResult.id
                        }
                    });
                    console.log('✅ Canvas updated with removed background VTO result');
                }

                if (activePersonImage?.id && activePersonImage?.image?.url && !activePersonImage?.removedBgImage) {
                    console.log('🔄 Also removing background from person image:', activePersonImage.id);
                    try {
                        const compressedFile = await compressForBackgroundRemoval(activePersonImage.image.url);
                        await removePersonBgFromUpload({
                            file: compressedFile,
                            personImageId: activePersonImage.id
                        }).unwrap();
                        console.log('✅ Person image background also removed');
                        if (refetchImages) {
                            refetchImages();
                        }
                    } catch (personBgError) {
                        console.error('Failed to remove person image background:', personBgError);
                    }
                }

                setShowBgRemovalProcessing(false);
                onClose();
            } else {
                if (!activePersonImage?.id || !activePersonImage?.image?.url) {
                    console.error('No active person image to remove background from');
                    setBgRemovalError('No image available to remove background from.');
                    return;
                }

                console.log('🔄 Removing background from person image:', activePersonImage.id);

                console.log('📦 Compressing image in frontend for background removal...');
                const compressedFile = await compressForBackgroundRemoval(activePersonImage.image.url);

                const result = await removePersonBgFromUpload({
                    file: compressedFile,
                    personImageId: activePersonImage.id
                }).unwrap();

                // Note: transformResponse returns data directly, not wrapped in personImage
                if (result?.removedBgImage?.url) {
                    console.log('✅ Person image background removed:', result.removedBgImage.url);

                    setVirtualTryOnResult({
                        image: result.removedBgImage.url,
                        originalImage: activePersonImage?.image?.url,
                        metadata: {
                            isBackgroundRemoved: true,
                            personImageId: result.id
                        }
                    });

                    if (refetchImages) {
                        refetchImages();
                    }

                    console.log('🔄 Closing modal after person image bg removal...');
                    setShowBgRemovalProcessing(false);
                    onClose();
                    console.log('✅ onClose() called');
                } else {
                    console.log('⚠️ No removedBgImage URL in result:', result);
                    setBgRemovalError('Background removal completed but no image was returned. Please try again.');
                }
            }

            if (refetchCredits) {
                refetchCredits();
            }

            console.log('✅ Background removal completed');
        } catch (err) {
            console.error('Remove background error:', err);
            setBgRemovalError(err?.data?.error || err?.message || 'Failed to remove background. Please try again.');
        } finally {
            setShowBgRemovalProcessing(false);
        }
    }, [activePersonImage, selectedVTOResult, removeVTOResultBackground, removePersonBgFromUpload,
        setVirtualTryOnResult, refetchImages, refetchCredits, onClose]);

    const handleToggleBackground = useCallback(async () => {
        if (!activePersonImage) {
            setBgRemovalError('Please select a person image first.');
            return;
        }

        const isLoggedIn = !!localStorage.getItem('token');
        if (!isLoggedIn) {
            setBgRemovalError('Please log in to toggle background.');
            return;
        }

        try {
            setIsRemovingBg(true);
            setBgRemovalError(null);

            const currentlyUsingRemoved = activePersonImage?.useRemovedImage !== false;
            const targetState = !currentlyUsingRemoved;

            console.log('🎯 Target state:', { currentlyUsingRemoved, targetState });

            if (selectedVTOResult?.id) {
                const vtoCurrentState = selectedVTOResult.useRemovedImage !== false;

                if (!selectedVTOResult.removedBgImage && targetState) {
                    console.log('🔄 VTO result needs background removal first');
                    await removeVTOResultBackground(selectedVTOResult.id).unwrap();
                    console.log('✅ VTO result background removed');
                    if (!targetState) {
                        await toggleVTOResultBackground(selectedVTOResult.id).unwrap();
                        console.log('✅ VTO result toggled to match person image');
                    }
                } else if (selectedVTOResult.removedBgImage && vtoCurrentState !== targetState) {
                    console.log('✅ Will toggle VTO result to match:', selectedVTOResult.id, 'from', vtoCurrentState, 'to', targetState);
                    await toggleVTOResultBackground(selectedVTOResult.id).unwrap();
                }
            }

            if (activePersonImage?.removedBgImage) {
                console.log('✅ Will toggle person image:', activePersonImage.id);
                const result = await togglePersonImageBackground(activePersonImage.id).unwrap();
                console.log('✅ Background toggled successfully:', result);

                if (!selectedVTOResult?.id) {
                    const imageUrl = result.useRemovedImage ? result.removedBgImage?.url : result.image?.url;
                    if (imageUrl) {
                        setPersonImage(imageUrl);
                    }
                    await setActivePersonImage(result.id);
                }
            }

            if (refetchImages) {
                await refetchImages();
            }

            onClose();
        } catch (err) {
            console.error('Toggle background error:', err);
            setBgRemovalError(err?.data?.error || err?.message || 'Failed to toggle background. Please try again.');
        } finally {
            setIsRemovingBg(false);
        }
    }, [activePersonImage, selectedVTOResult, togglePersonImageBackground, toggleVTOResultBackground,
        removeVTOResultBackground, setPersonImage, setActivePersonImage, refetchImages, onClose]);

    const handleSelectPersonImage = useCallback(async (img) => {
        try {
            console.log('🔄 Selecting person image from history:', img);

            setBgRemovalError(null);

            dispatch(clearTryOnItems());
            dispatch(clearSelectedVTOResult());
            revertToOriginal();

            const isLoggedIn = !!localStorage.getItem('token');

            if (!isLoggedIn) {
                const shouldUseRemoved = img.useRemovedImage !== false && img.removedBgImage?.url;
                const imageUrl = shouldUseRemoved ? img.removedBgImage.url : img.image?.url;

                setPersonImage({
                    image: imageUrl,
                    metadata: {
                        fileName: img.fileName || 'guest-image',
                        backgroundRemoved: !!img.removedBgImage,
                        originalDimensions: {
                            width: 0,
                            height: 0
                        }
                    }
                });

                setCanvasIntegrationMode(true);
                console.log('✅ Guest person image selected and canvas integration enabled');
            } else {
                await setActivePersonImage(img.id);
                setCanvasIntegrationMode(true);
                console.log('✅ Person image selected, wearing items cleared, and canvas integration enabled');
            }

            setTimeout(() => onClose(), 300);
        } catch (error) {
            console.error('❌ Failed to select person image:', error);
        }
    }, [dispatch, revertToOriginal, setPersonImage, setCanvasIntegrationMode, setActivePersonImage, onClose]);

    const handleConfirmDelete = useCallback(async () => {
        if (imageToDelete) {
            try {
                setDeleteError(null);

                const isLoggedIn = !!localStorage.getItem('token');

                if (!isLoggedIn) {
                    const guestImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');
                    const updatedImages = guestImages.filter(img => img.id !== imageToDelete.id);
                    localStorage.setItem('guestPersonImages', JSON.stringify(updatedImages));

                    setDeleteConfirmOpen(false);
                    setImageToDelete(null);
                } else {
                    await deletePersonImage(imageToDelete.id);
                    setDeleteConfirmOpen(false);
                    setImageToDelete(null);
                }
            } catch (error) {
                console.error('Delete error:', error);
                const errorMessage = error?.data?.message ||
                    error?.message ||
                    'Failed to delete person image';
                setDeleteError(errorMessage);
            }
        }
    }, [imageToDelete, deletePersonImage]);

    const handleCancelDelete = useCallback(() => {
        setDeleteConfirmOpen(false);
        setImageToDelete(null);
        setDeleteError(null);
    }, []);

    const handleCancelDeletionWarning = useCallback(() => {
        setShowDeletionWarning(false);
        setImageToDelete(null);
        setVtoCount(0);
        setDeletionError(null);
    }, []);

    const handleConfirmCascadeDelete = useCallback(async () => {
        if (!imageToDelete) return;

        try {
            setDeletionError(null);

            await deleteWithCascade(imageToDelete.id, 'person-image');

            if (activePersonImageId === imageToDelete.id) {
                clearActivePersonImage();
                dispatch(clearTryOnItems());
                dispatch(clearSelectedVTOResult());
            }

            if (refetchImages) {
                await refetchImages();
            }

            setShowDeletionWarning(false);
            setImageToDelete(null);
            setVtoCount(0);

            console.log('✅ Cascade delete completed successfully');
        } catch (error) {
            console.error('❌ Cascade delete failed:', error);
            setDeletionError(error.message || 'Failed to delete person image and VTO results');
        }
    }, [imageToDelete, activePersonImageId, deleteWithCascade, clearActivePersonImage, dispatch, refetchImages]);

    const handleSetDefault = useCallback(async () => {
        console.log('🔘 handleSetDefault called', { activePersonImage });

        if (!activePersonImage) {
            console.log('❌ No active person image');
            setDefaultError('Please select a person image first.');
            return;
        }

        setDefaultError(null);
        setIsSettingDefault(true);

        const isLoggedIn = !!localStorage.getItem('token');
        console.log('🔐 Is logged in:', isLoggedIn);

        try {
            if (!isLoggedIn) {
                const guestImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');
                const updatedImages = guestImages.map(img => ({
                    ...img,
                    isDefault: img.id === activePersonImage.id
                }));
                localStorage.setItem('guestPersonImages', JSON.stringify(updatedImages));

                console.log('✅ Guest default image updated in localStorage');

                if (refetchImages) {
                    await refetchImages();
                }
            } else {
                console.log('📡 Calling setDefaultPersonImage API with ID:', activePersonImage.id);
                const result = await setDefaultPersonImage(activePersonImage.id).unwrap();
                console.log('✅ Person image set as default, result:', result);
            }
        } catch (error) {
            console.error('❌ Failed to set default:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.status,
                data: error.data
            });

            let errorMessage = 'Failed to set default image. Please try again.';

            if (error.status === 403) {
                errorMessage = 'You do not have permission to set this image as default.';
            } else if (error.status === 404) {
                errorMessage = 'Image not found. Please refresh and try again.';
            } else if (error.status === 401) {
                errorMessage = 'Please log in to set a default image.';
            } else if (error.data?.message) {
                errorMessage = error.data.message;
            }

            setDefaultError(errorMessage);
        } finally {
            setIsSettingDefault(false);
        }
    }, [activePersonImage, setDefaultPersonImage, refetchImages]);

    const handleSaveName = useCallback(async () => {
        if (!activePersonImage || !tempName.trim()) {
            setEditingName(false);
            return;
        }

        setNameError(null);
        setIsSavingName(true);

        const isLoggedIn = !!localStorage.getItem('token');

        try {
            if (!isLoggedIn) {
                const guestImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');
                const updatedImages = guestImages.map(img =>
                    img.id === activePersonImage.id
                        ? { ...img, displayName: tempName.trim() }
                        : img
                );
                localStorage.setItem('guestPersonImages', JSON.stringify(updatedImages));

                if (refetchImages) {
                    await refetchImages();
                }

                setEditingName(false);
                setTempName('');
            } else {
                await updatePersonImageName({
                    id: activePersonImage.id,
                    displayName: tempName.trim()
                }).unwrap();
                console.log('✅ Person image name updated');

                setEditingName(false);
                setTempName('');
            }
        } catch (error) {
            console.error('❌ Failed to update name:', error);

            let errorMessage = 'Failed to update name. Please try again.';

            if (error.status === 403) {
                errorMessage = 'You do not have permission to update this image name.';
            } else if (error.status === 404) {
                errorMessage = 'Image not found. Please refresh and try again.';
            } else if (error.status === 401) {
                errorMessage = 'Please log in to update image names.';
            } else if (error.data?.message) {
                errorMessage = error.data.message;
            }

            setNameError(errorMessage);
        } finally {
            setIsSavingName(false);
        }
    }, [activePersonImage, tempName, updatePersonImageName, refetchImages]);

    const handleStartEditName = useCallback(() => {
        setTempName(activePersonImage?.displayName || '');
        setEditingName(true);
    }, [activePersonImage]);

    const handleOpenEditor = useCallback(() => {
        if (!activePersonImage) return;
        const imageUrl = activePersonImage.useRemovedImage && activePersonImage.removedBgImage?.url
            ? activePersonImage.removedBgImage.url
            : activePersonImage.image?.url;
        setEditorImageUrl(imageUrl);
        setIsEditorOpen(true);
    }, [activePersonImage]);

    const handleEditorSave = useCallback(async (blob) => {
        try {
            setIsSavingCrop(true);
            const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });

            const isLoggedIn = !!localStorage.getItem('token');

            if (!isLoggedIn) {
                const reader = new FileReader();
                reader.onloadend = async () => {
                    const imageData = reader.result;
                    const guestPersonImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');

                    const newImage = {
                        id: Date.now(),
                        image: { url: imageData },
                        fileName: 'cropped-image.jpg',
                        createdAt: new Date().toISOString(),
                        backgroundRemoved: false
                    };

                    guestPersonImages.unshift(newImage);
                    localStorage.setItem('guestPersonImages', JSON.stringify(guestPersonImages));

                    setCanvasIntegrationMode(true);

                    if (refetchImages) {
                        await refetchImages();
                    }

                    setIsEditorOpen(false);
                    setIsSavingCrop(false);
                    console.log('✅ Cropped image saved to localStorage');

                    setTimeout(() => onClose(), 300);
                };
                reader.readAsDataURL(file);
                return;
            }

            console.log('🔄 Uploading cropped image...', { uploadPersonImage: typeof uploadPersonImage });

            const result = await uploadPersonImage({
                file,
                fileName: 'cropped-image.jpg',
                backgroundRemoved: false
            });

            console.log('✅ Cropped image uploaded:', result);

            if (result?.id && result.image?.url) {
                await setActivePersonImage(result.id);
                setCanvasIntegrationMode(true);

                console.log('✅ Cropped image set as active:', result.id);

                setIsEditorOpen(false);
                setIsSavingCrop(false);

                setTimeout(() => onClose(), 300);
            } else {
                console.error('❌ Invalid result from upload:', result);
                setIsSavingCrop(false);
            }
        } catch (error) {
            console.error('Failed to save cropped image:', error);
            setIsSavingCrop(false);
        }
    }, [uploadPersonImage, setActivePersonImage, setCanvasIntegrationMode, refetchImages, onClose]);

    const handleDeleteClick = useCallback(async (e, img) => {
        e.stopPropagation();
        console.log('🗑️ Delete button clicked for person image:', img.id);
        setImageToDelete(img);

        const isLoggedIn = !!localStorage.getItem('token');
        console.log('🔐 User logged in:', isLoggedIn);

        if (isLoggedIn) {
            try {
                console.log('📊 Checking VTO count...');
                const count = await checkVTOCount(img.id, 'person-image');
                console.log('✅ VTO count received:', count);

                if (count > 0) {
                    console.log('⚠️ Showing deletion warning modal');
                    setVtoCount(count);
                    setShowDeletionWarning(true);
                } else {
                    console.log('✓ No VTO results, showing normal delete modal');
                    setDeleteConfirmOpen(true);
                }
            } catch (error) {
                console.error('❌ Error checking VTO count:', error);
                setDeleteConfirmOpen(true);
            }
        } else {
            setDeleteConfirmOpen(true);
        }
    }, [checkVTOCount]);

    // Bulk delete state - stored at function scope for access by handlers
    const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
    const [bulkVtoCount, setBulkVtoCount] = useState(0);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [showBulkDeletionWarning, setShowBulkDeletionWarning] = useState(false);
    const [bulkDeletionError, setBulkDeletionError] = useState(null);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Handler to initiate bulk delete - shows appropriate confirmation modal
    const handleBulkDeleteClick = useCallback(async (imageIds) => {
        if (!imageIds || imageIds.length === 0) return;

        console.log('🗑️ Bulk delete clicked for', imageIds.length, 'images');
        setBulkDeleteIds(imageIds);
        setBulkDeletionError(null);

        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
            // Guest mode: show simple confirmation
            setShowBulkDeleteConfirm(true);
            return;
        }

        // Logged-in user: check total VTO count for all selected images
        try {
            let totalVtoCount = 0;
            for (const imageId of imageIds) {
                const count = await checkVTOCount(imageId, 'person-image');
                totalVtoCount += count;
            }

            console.log('📊 Total VTO count for bulk delete:', totalVtoCount);
            setBulkVtoCount(totalVtoCount);

            if (totalVtoCount > 0) {
                // Has VTO results - show warning modal
                setShowBulkDeletionWarning(true);
            } else {
                // No VTO results - show simple confirmation
                setShowBulkDeleteConfirm(true);
            }
        } catch (error) {
            console.error('Error checking VTO counts:', error);
            // On error, show simple confirmation
            setShowBulkDeleteConfirm(true);
        }
    }, [checkVTOCount]);

    // Confirm bulk delete (no VTO results)
    const handleConfirmBulkDelete = useCallback(async () => {
        if (bulkDeleteIds.length === 0) return;

        setIsBulkDeleting(true);
        setBulkDeletionError(null);

        const isLoggedIn = !!localStorage.getItem('token');

        try {
            if (!isLoggedIn) {
                // Guest mode: delete from localStorage
                const guestImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');
                const updatedImages = guestImages.filter(img => !bulkDeleteIds.includes(img.id));
                localStorage.setItem('guestPersonImages', JSON.stringify(updatedImages));
                console.log('✅ Bulk deleted', bulkDeleteIds.length, 'guest images');
            } else {
                // Delete each image
                for (const imageId of bulkDeleteIds) {
                    try {
                        await deletePersonImage(imageId);

                        // Clear active if deleted
                        if (activePersonImageId === imageId) {
                            clearActivePersonImage();
                            dispatch(clearTryOnItems());
                            dispatch(clearSelectedVTOResult());
                        }
                    } catch (err) {
                        console.error(`Failed to delete image ${imageId}:`, err);
                    }
                }
            }

            if (refetchImages) {
                await refetchImages();
            }

            console.log('✅ Bulk delete completed');
            setShowBulkDeleteConfirm(false);
            setBulkDeleteIds([]);
        } catch (error) {
            console.error('❌ Bulk delete failed:', error);
            setBulkDeletionError(error.message || 'Failed to delete images');
        } finally {
            setIsBulkDeleting(false);
        }
    }, [bulkDeleteIds, deletePersonImage, activePersonImageId, clearActivePersonImage, dispatch, refetchImages]);

    // Confirm bulk cascade delete (has VTO results)
    const handleConfirmBulkCascadeDelete = useCallback(async () => {
        if (bulkDeleteIds.length === 0) return;

        setIsBulkDeleting(true);
        setBulkDeletionError(null);

        try {
            for (const imageId of bulkDeleteIds) {
                try {
                    await deleteWithCascade(imageId, 'person-image');

                    // Clear active if deleted
                    if (activePersonImageId === imageId) {
                        clearActivePersonImage();
                        dispatch(clearTryOnItems());
                        dispatch(clearSelectedVTOResult());
                    }
                } catch (err) {
                    console.error(`Failed to cascade delete image ${imageId}:`, err);
                }
            }

            if (refetchImages) {
                await refetchImages();
            }

            console.log('✅ Bulk cascade delete completed');
            setShowBulkDeletionWarning(false);
            setBulkDeleteIds([]);
            setBulkVtoCount(0);
        } catch (error) {
            console.error('❌ Bulk cascade delete failed:', error);
            setBulkDeletionError(error.message || 'Failed to delete images and VTO results');
        } finally {
            setIsBulkDeleting(false);
        }
    }, [bulkDeleteIds, deleteWithCascade, activePersonImageId, clearActivePersonImage, dispatch, refetchImages]);

    // Cancel bulk delete
    const handleCancelBulkDelete = useCallback(() => {
        setShowBulkDeleteConfirm(false);
        setShowBulkDeletionWarning(false);
        setBulkDeleteIds([]);
        setBulkVtoCount(0);
        setBulkDeletionError(null);
    }, []);

    // Computed values
    const isOperationInProgress = isPhotoUploading || isUploading || isSettingDefault || isSavingName || isRemovingBg || isRemovingBgUpload || isTogglingBg || isDeleting;

    return {
        // Refs
        fileInputRef,

        // State
        deleteConfirmOpen,
        imageToDelete,
        deleteError,
        bgRemovalError,
        setBgRemovalError,
        editingName,
        tempName,
        setTempName,
        defaultError,
        setDefaultError,
        nameError,
        setNameError,
        isEditorOpen,
        setIsEditorOpen,
        editorImageUrl,
        isSavingCrop,
        showDeletionWarning,
        vtoCount,
        deletionError,
        showBgRemovalProcessing,

        // Computed
        isPhotoUploading,
        isOperationInProgress,
        hasPersonImage,
        isDeletingWithCascade,
        isLoadingImages,

        // Data
        personImages,
        activePersonImage,
        activePersonImageId,
        isCanvasIntegrated,
        creditBalance,
        hasCredits,
        daysUntilReset,

        // Bulk delete state
        bulkDeleteIds,
        bulkVtoCount,
        showBulkDeleteConfirm,
        showBulkDeletionWarning,
        bulkDeletionError,
        isBulkDeleting,

        // Handlers
        handleFileInputClick,
        handleLocalFileUpload,
        handleTakePicture,
        handleRemovePersonImage,
        handleRemoveBackgroundClick,
        handleToggleBackground,
        handleSelectPersonImage,
        handleConfirmDelete,
        handleCancelDelete,
        handleCancelDeletionWarning,
        handleConfirmCascadeDelete,
        handleSetDefault,
        handleSaveName,
        handleStartEditName,
        handleOpenEditor,
        handleEditorSave,
        handleDeleteClick,
        handleBulkDeleteClick,
        handleConfirmBulkDelete,
        handleConfirmBulkCascadeDelete,
        handleCancelBulkDelete
    };
};

export default usePersonImageModalHandlers;
